import { useTranslation } from 'react-i18next'
import { Ban, Check, Clock3, Euro, FileText, Pencil, Send } from 'lucide-react'
import {
  INVOICE_DETAIL_TAB_STALE_TIME,
  INVOICE_HISTORY_QUERY,
  useInvoiceHistory,
} from '@/hooks/useInvoices'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { ActivityLog, InvoiceDetail } from '@/types'
import {
  InvoiceTabSectionCard,
  InvoiceTabSectionHeading,
} from './InvoiceTabSection'
import { activityTitle } from './invoiceHistoryActivity'
import { formatInvoiceAmount } from './invoiceTabStyles'

const HIDDEN_EVENT_TYPES = new Set(['document.uploaded'])

function stringProp(
  properties: ActivityLog['properties'],
  key: string
): string | null {
  const value = properties?.[key]
  return typeof value === 'string' && value.trim() ? value : null
}

function sentRecipient(description: string | null): string | null {
  if (!description) return null
  const matched = description.match(/sent to\s+(.+)$/i)
  return matched?.[1]?.trim() || null
}

function eventDescription(item: ActivityLog): string | null {
  const description = item.description?.trim()
  if (!description) return item.status_detail || null

  const genericDescriptions = new Set([
    'Invoice created',
    'Invoice updated',
    'Invoice validated',
    'Note added',
    'Invoice marked paid from payment allocation',
    'Invoice marked paid (bulk)',
    'Credit note created',
    'Credit note updated',
    'Credit note deleted',
    'Payment proof uploaded',
    'Stripe payment failed',
  ])

  return genericDescriptions.has(description)
    ? (item.status_detail ?? null)
    : description
}

function activityVisual(eventType: string) {
  if (eventType === 'invoice.paid') {
    return {
      bgClassName: 'bg-[#d1fae5]',
      iconClassName: 'text-[#059669]',
      icon: <Check className='size-4' strokeWidth={2.4} />,
    }
  }

  if (eventType.startsWith('payment.')) {
    return {
      bgClassName: 'bg-[#d1fae5]',
      iconClassName: 'text-[#059669]',
      icon: <Euro className='size-4' strokeWidth={2.25} />,
    }
  }

  if (
    eventType === 'invoice.sent' ||
    eventType === 'invoice.reminder_sent' ||
    eventType === 'credit_note.sent'
  ) {
    return {
      bgClassName: 'bg-[#fff1ea]',
      iconClassName: 'text-[#ff5a1f]',
      icon: <Send className='size-4' strokeWidth={2.2} />,
    }
  }

  if (eventType === 'invoice.updated' || eventType === 'credit_note.updated') {
    return {
      bgClassName: 'bg-[#fff1ea]',
      iconClassName: 'text-[#ff5a1f]',
      icon: <Pencil className='size-4' strokeWidth={2.2} />,
    }
  }

  if (
    eventType === 'invoice.cancelled' ||
    eventType === 'credit_note.deleted' ||
    eventType === 'invoice.deleted'
  ) {
    return {
      bgClassName: 'bg-[#fde9e8]',
      iconClassName: 'text-[#d63b33]',
      icon: <Ban className='size-4' strokeWidth={2.2} />,
    }
  }

  if (
    eventType.startsWith('invoice.') ||
    eventType.startsWith('credit_note.')
  ) {
    return {
      bgClassName: 'bg-[#fff1ea]',
      iconClassName: 'text-[#ff5a1f]',
      icon: <FileText className='size-4' strokeWidth={2.2} />,
    }
  }

  return {
    bgClassName: 'bg-[#f4f5f8]',
    iconClassName: 'text-[#272c36]',
    icon: <Clock3 className='size-4' strokeWidth={2.2} />,
  }
}

function activityDetail(
  item: ActivityLog,
  invoice: InvoiceDetail,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null {
  const method = stringProp(item.properties, 'method')
  const methodLabel = method
    ? t(`enums:paymentMethod.${method}`, { defaultValue: method })
    : null

  switch (item.event_type) {
    case 'invoice.paid':
      return t('detail.history.notes.invoicePaid')
    case 'invoice.created':
      return t('detail.history.notes.totalInclVat', {
        amount: formatInvoiceAmount(invoice.total),
      })
    case 'invoice.auto_generated': {
      const source = stringProp(item.properties, 'source')
      if (source === 'job' && invoice.number) {
        return t('detail.history.notes.invoiceAutoGeneratedWithNumber', {
          number: invoice.number,
        })
      }

      return t('detail.history.notes.proFormaInvoiceAutoGenerated')
    }
    case 'invoice.sent':
      return sentRecipient(item.description)
    case 'payment.received':
    case 'payment.pending':
    case 'payment.onsite_cash':
    case 'payment.onsite_qr':
    case 'payment.created':
    case 'payment.superseded':
      return methodLabel
    case 'payment.status_changed': {
      const oldStatus = stringProp(item.properties, 'old')
      const newStatus = stringProp(item.properties, 'new')
      if (!oldStatus || !newStatus) return item.description
      const oldStatusLabel = t(`detail.history.paymentStatuses.${oldStatus}`, {
        defaultValue: oldStatus,
      })
      const newStatusLabel = t(`detail.history.paymentStatuses.${newStatus}`, {
        defaultValue: newStatus,
      })
      return t('detail.history.notes.statusChanged', {
        oldStatus: oldStatusLabel,
        newStatus: newStatusLabel,
      })
    }
    default:
      return eventDescription(item)
  }
}

function HistoryRow({
  item,
  invoice,
}: {
  item: ActivityLog
  invoice: InvoiceDetail
}) {
  const { t } = useTranslation(['invoices', 'enums'])
  const visual = activityVisual(item.event_type)
  const title = activityTitle(item, t)
  const detail = activityDetail(item, invoice, t)

  return (
    <div className='flex w-full items-start gap-3 overflow-hidden rounded-xl border border-[#e7e9ef] bg-white p-3'>
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg',
          visual.bgClassName,
          visual.iconClassName
        )}
      >
        {visual.icon}
      </span>

      <div className='min-w-0 flex-1'>
        <p className='text-sm font-medium leading-[1.4] text-[#171a20]'>
          {title}
        </p>
        <div className='mt-1 flex flex-wrap items-center gap-2 text-sm font-normal leading-[1.4] text-[#3d434e]'>
          <span>{formatDateTime(item.timestamp ?? item.created_at)}</span>
          {detail ? (
            <>
              <span
                className='size-1 shrink-0 rounded-full bg-[#8f95a6]'
                aria-hidden
              />
              <span className='min-w-0'>{detail}</span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export function InvoiceHistoryTab({ invoice }: { invoice: InvoiceDetail }) {
  const { t } = useTranslation('invoices')
  const { data, isLoading, isError } = useInvoiceHistory(
    invoice.id,
    INVOICE_HISTORY_QUERY,
    { staleTime: INVOICE_DETAIL_TAB_STALE_TIME }
  )
  const history = (data?.data ?? []).filter(
    item => !HIDDEN_EVENT_TYPES.has(item.event_type)
  )

  return (
    <InvoiceTabSectionCard>
      <InvoiceTabSectionHeading
        title={t('detail.history.title')}
        icon={<Clock3 className='size-4' strokeWidth={2.2} />}
      />
      <div className='px-6 pb-6 pt-4'>
        {isLoading ? (
          <p className='py-6 text-center text-sm text-muted-foreground'>
            {t('detail.loading')}
          </p>
        ) : isError ? (
          <p className='py-6 text-center text-sm text-destructive'>
            {t('detail.history.loadError')}
          </p>
        ) : history.length === 0 ? (
          <p className='py-6 text-center text-sm text-muted-foreground'>
            {t('detail.noHistory')}
          </p>
        ) : (
          <div className='flex flex-col gap-2'>
            {history.map(item => (
              <HistoryRow key={item.id} item={item} invoice={invoice} />
            ))}
          </div>
        )}
      </div>
    </InvoiceTabSectionCard>
  )
}
